import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Navigation, Loader2, MapPin, Play, Pause } from 'lucide-react';
import locationService from '../services/LocationService';
import { useGeo } from '../context/GeoContext';
import { formatSpeed } from '../lib/i18n';
import BackgroundLocationDisclosureDialog, {
  BackgroundLocationSettingsPrompt,
  hasBackgroundLocationConsent,
  setBackgroundLocationConsent,
} from './BackgroundLocationDisclosureDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationTracker = ({ bookingId, isActive, onLocationUpdate }) => {
  const { country } = useGeo();

  // Demo/recording helper: visiting any URL with ?reset_bg_consent=1 clears
  // the persisted disclosure consent so the prominent disclosure modal fires
  // again on the next Start Sharing tap. Used when recording the Play Console
  // demo video. Runs once on mount, clears the flag, then fires a toast.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('reset_bg_consent') === '1') {
        setBackgroundLocationConsent(false);
        toast.info('Background-location disclosure will re-appear on next tap');
      }
    } catch { /* ignore */ }
  }, []);

  // Check localStorage for tracking state for this booking
  const getTrackingState = () => {
    const stored = localStorage.getItem(`location_tracking_${bookingId}`);
    return stored === 'active';
  };
  
  const [tracking, setTracking] = useState(false);
  const [wasTracking, setWasTracking] = useState(getTrackingState); // Was tracking before card closed
  const [currentLocation, setCurrentLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [settingsPromptOpen, setSettingsPromptOpen] = useState(false);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const hasAutoStartedRef = useRef(false);

  const getToken = () => localStorage.getItem('rollm8s_token') || localStorage.getItem('token');

  const sendLocationUpdate = useCallback(async (position) => {
    const locationData = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      heading: position.coords.heading,
      speed: position.coords.speed ? position.coords.speed * 3.6 : 0 // Convert m/s to km/h
    };

    setCurrentLocation(locationData);
    onLocationUpdate?.(locationData);

    try {
      const token = getToken();
      await axios.post(
        `${API}/tracking/update-location?booking_id=${bookingId}`,
        locationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to send location update:', error);
    }
  }, [bookingId, onLocationUpdate]);

  const runTracking = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Request upfront permission (important on native)
      await locationService.requestPermissions();

      // First get current position to show immediately
      const position = await locationService.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
      sendLocationUpdate(position);
      setLoading(false);
      setTracking(true);
      setWasTracking(true);
      localStorage.setItem(`location_tracking_${bookingId}`, 'active');

      // Start a background-capable watcher. On native this keeps emitting
      // updates even when the app is minimised (ride-share will not drop).
      // On web it gracefully falls back to navigator.geolocation.watchPosition.
      watchIdRef.current = await locationService.startBackgroundTracking(
        (pos) => sendLocationUpdate(pos),
        {
          enableHighAccuracy: true,
          minimumUpdateInterval: 2000,
          backgroundTitle: 'RollM8s Ride',
          backgroundMessage: 'Sharing your live location with your passengers',
        }
      );

      // Fallback interval for updates every 10 seconds (web only — redundant
      // on native but harmless).
      intervalRef.current = setInterval(async () => {
        try {
          const p = await locationService.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 5000,
          });
          sendLocationUpdate(p);
        } catch { /* ignore */ }
      }, 10000);

      toast.success('Location sharing started');

      // Android 11+ can't grant "Allow all the time" via a runtime prompt —
      // only via Settings. Nudge the user to open Settings so background
      // tracking actually survives an app minimise. Fires ~1 s after the
      // watcher starts so the user sees their foreground tracking work first.
      if (locationService.isNative()) {
        setTimeout(() => setSettingsPromptOpen(true), 1200);
      }
    } catch (err) {
      setLoading(false);
      setError(err?.message || 'Could not start location tracking');
      toast.error('Could not get location: ' + (err?.message || 'unknown error'));
    }
  }, [sendLocationUpdate, bookingId]);

  const startTracking = useCallback(async () => {
    if (!navigator.geolocation && !locationService.isNative()) {
      setError('Geolocation is not supported by your browser');
      toast.error('Geolocation not supported');
      return;
    }

    // Google Play policy: show prominent disclosure BEFORE any OS prompt
    // for ACCESS_BACKGROUND_LOCATION. Persist consent so it only fires once.
    if (!hasBackgroundLocationConsent()) {
      setDisclosureOpen(true);
      return;
    }

    await runTracking();
  }, [runTracking]);

  const handleDisclosureAllow = useCallback(async () => {
    setBackgroundLocationConsent(true);
    setDisclosureOpen(false);
    await runTracking();
  }, [runTracking]);

  const handleDisclosureCancel = useCallback(() => {
    setDisclosureOpen(false);
    toast.info('Location sharing cancelled');
  }, []);

  const handleOpenSettings = useCallback(async () => {
    setSettingsPromptOpen(false);
    try {
      if (locationService.isNative()) {
        const { BackgroundGeolocation } = await import('@capacitor-community/background-geolocation');
        await BackgroundGeolocation.openSettings();
      } else {
        toast.info('Open your browser settings to manage location permissions');
      }
    } catch (e) {
      toast.error('Could not open settings — please open them manually');
    }
  }, []);

  const handleSettingsDismiss = useCallback(() => {
    setSettingsPromptOpen(false);
  }, []);

  const stopTracking = useCallback(async () => {
    if (watchIdRef.current !== null && watchIdRef.current !== undefined) {
      await locationService.stopBackgroundTracking(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTracking(false);
    setWasTracking(false);
    localStorage.removeItem(`location_tracking_${bookingId}`);
    toast.info('Location sharing stopped');
  }, [bookingId]);

  // Auto-start tracking when component becomes active
  useEffect(() => {
    // Reset the auto-start flag when component mounts
    hasAutoStartedRef.current = false;
  }, []);
  
  useEffect(() => {
    // Auto-resume tracking if it was active before (card was closed while sharing)
    // Or auto-start if this is first time and component is active
    if (isActive && !hasAutoStartedRef.current && !tracking && !loading) {
      if (wasTracking) {
        // Resume tracking - it was active before card was closed
        hasAutoStartedRef.current = true;
        const timer = setTimeout(() => {
          startTracking();
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isActive, wasTracking, tracking, loading, startTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null && watchIdRef.current !== undefined) {
        locationService.stopBackgroundTracking(watchIdRef.current);
      }
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Don't render if not active
  if (!isActive) return null;

  return (
    <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Navigation className="w-5 h-5 text-blue-500" />
            {(tracking || wasTracking) && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-blue-500">
              {loading ? 'Resuming location...' : tracking ? 'Sharing location' : wasTracking ? 'Reconnecting...' : 'Location sharing off'}
            </p>
            {currentLocation && (
              <p className="text-xs text-muted-foreground font-mono">
                {currentLocation.lat.toFixed(5)}, {currentLocation.lng.toFixed(5)}
              </p>
            )}
          </div>
        </div>

        {currentLocation && currentLocation.speed > 0 && (() => {
          // currentLocation.speed is km/h — convert back to mps for the shared helper
          const mps = currentLocation.speed / 3.6;
          const formatted = formatSpeed(mps, { countryCode: country });
          const [value, unit] = formatted.split(' ');
          return (
            <div className="text-right">
              <p className="text-lg font-bold text-blue-500">{value}</p>
              <p className="text-xs text-muted-foreground">{unit}</p>
            </div>
          );
        })()}
      </div>

      {error && (
        <p className="text-xs text-destructive bg-destructive/10 p-2 rounded">{error}</p>
      )}

      {/* Manual control button */}
      <Button
        variant={(tracking || wasTracking) ? "outline" : "default"}
        size="sm"
        onClick={(tracking || wasTracking) ? stopTracking : startTracking}
        disabled={loading}
        className="w-full"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {wasTracking ? 'Resuming...' : 'Getting location...'}
          </>
        ) : (tracking || wasTracking) ? (
          <>
            <Pause className="w-4 h-4 mr-2" />
            Stop Sharing Location
          </>
        ) : (
          <>
            <Play className="w-4 h-4 mr-2" />
            Start Sharing Location
          </>
        )}
      </Button>
      
      {!tracking && !wasTracking && (
        <p className="text-xs text-center text-muted-foreground">
          Tap to share your location with the passenger
        </p>
      )}

      <BackgroundLocationDisclosureDialog
        open={disclosureOpen}
        onAllow={handleDisclosureAllow}
        onCancel={handleDisclosureCancel}
      />

      <BackgroundLocationSettingsPrompt
        open={settingsPromptOpen}
        onOpenSettings={handleOpenSettings}
        onDismiss={handleSettingsDismiss}
      />
    </div>
  );
};

export default LocationTracker;
